import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { ApiError, lotKeyOf, lotRefOfSummary } from '@neon-one/auctions-types';
import type {
  PublicItemSummary,
  PublicItemDetail,
  PublicBidEntry,
  Category,
  WsClientFrame,
} from '@neon-one/auctions-types';
import type { SharedKernel } from '@/shell';
import { useSession, historyHasBid } from '@/shell';

import { catalogApi, type CatalogQuery } from './api';

// `Kernel` documents the kernel surface this feature relies on. Deep
// imports of shell internals are blocked by lint; this re-export is the
// feature's contract pin.
export type Kernel = SharedKernel;

type LoadState = 'idle' | 'loading' | 'success' | 'error' | 'not-found';

interface Filters {
  q: string;
  categoryId: number | null;
  minBid: number | null;
  maxBid: number | null;
}

const PAGE_SIZE = 12;
// Server-side cap on `limit` (domain/catalog.ts). refreshItems pages through
// in chunks of this size to re-fetch a window larger than one request allows.
const MAX_PAGE_SIZE = 200;
const INVALID_CURSOR_STATUS = 400;
let bidCountsSeq = 0;

function appendUniqueLots(
  loaded: PublicItemSummary[],
  incoming: PublicItemSummary[],
): PublicItemSummary[] {
  const seen = new Set(loaded.map((lot) => lotKeyOf(lotRefOfSummary(lot))));
  const fresh = incoming.filter((lot) => {
    const key = lotKeyOf(lotRefOfSummary(lot));
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return [...loaded, ...fresh];
}

// ApiError is the ONLY shape a "this entity does not exist" failure can
// arrive in: createApiClient's interceptor collapses every HTTP failure into
// one, and it carries `.status` directly — there is no axios `.response`
// wrapper to read through, so probing for one silently never matches
// (GT1-301).
//
// Do not widen this to any rejection that looks 404-ish. The interceptor
// rethrows the bare AxiosError when the request got no response at all
// (offline, DNS, TLS abort, client timeout); that is transient, and the
// 'error' state's "try refreshing the page" is the correct advice for it,
// whereas 'not-found' tells the bidder to give up on the URL entirely.
function isNotFound(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

export const useCatalogStore = defineStore('catalog', () => {
  const session = useSession();
  const slug = ref<string | null>(null);
  const items = ref<PublicItemSummary[]>([]);
  const categories = ref<Category[]>([]);
  const detail = ref<PublicItemDetail | null>(null);
  const nextCursor = ref<string | null>(null);
  const itemsState = ref<LoadState>('idle');
  const detailState = ref<LoadState>('idle');
  const error = ref<string | null>(null);
  // Appending a page is tracked apart from itemsState so the list already on
  // screen keeps its 'success' rendering (count row and all) while the next
  // page loads beneath it. Flipping itemsState would shift the content above
  // the bidder's scroll position on every page.
  const loadingMore = ref(false);
  const loadMoreFailed = ref(false);

  const filters = ref<Filters>({ q: '', categoryId: null, minBid: null, maxBid: null });

  // Which slug + filters `items` was loaded for. CatalogPage remounts on
  // every return from an item page, and reusing the list it left (instead
  // of starting again from page one) is what keeps the bidder's place in it.
  const loadedFor = ref<string | null>(null);

  function listKey(eventSlug: string): string {
    const { q, categoryId, minBid, maxBid } = filters.value;
    return JSON.stringify([eventSlug, q, categoryId, minBid, maxBid]);
  }

  function hasLoadedList(eventSlug: string): boolean {
    return (
      itemsState.value === 'success' &&
      items.value.length > 0 &&
      loadedFor.value === listKey(eventSlug)
    );
  }

  // GT1-442 (bid-void-design.md §6) — live "Removed" context for the open
  // detail. Both are session-scoped to the currently-loaded detail and are
  // cleared whenever it (re)loads.
  //
  // `myBidRemoved` drives the plain-language notice near the bid bar. It is
  // set only when the removed bid belonged to the viewing bidder.
  //
  // `removedBidKeys` marks bid-history rows so a removed bid is visibly struck
  // instead of silently vanishing. The public history has no bid id
  // (PublicBidEntry = amount + handle + placedAt + isMine) and the server
  // excludes voided bids from it on the next fetch, so rows are matched on
  // "mine?" + amount — unique in practice, since every bid on an item must
  // clear the previous high by at least one increment. Keyed, not written onto
  // the entry, so the wire type stays untouched.
  const myBidRemoved = ref(false);
  const removedBidKeys = ref<Set<string>>(new Set());
  // Void actions already applied — one action fans out on item:{id} AND
  // event:{uuid}, and the detail page subscribes to both.
  const handledVoidBidIds = new Set<number>();

  function historyKey(mine: boolean, amountCents: number): string {
    return `${mine ? 'me' : 'other'}:${amountCents}`;
  }

  function isBidHistoryEntryRemoved(entry: PublicBidEntry): boolean {
    return removedBidKeys.value.has(historyKey(entry.isMine, entry.amountCents));
  }

  function clearRemovedContext(): void {
    myBidRemoved.value = false;
    if (removedBidKeys.value.size > 0) removedBidKeys.value = new Set();
    handledVoidBidIds.clear();
  }

  const filtersActive = computed(
    () =>
      filters.value.q.length > 0 ||
      filters.value.categoryId != null ||
      filters.value.minBid != null ||
      filters.value.maxBid != null,
  );

  function buildQuery(cursor?: string | null, limit: number = PAGE_SIZE): CatalogQuery {
    return {
      category: filters.value.categoryId ?? undefined,
      q: filters.value.q || undefined,
      minBid: filters.value.minBid ?? undefined,
      maxBid: filters.value.maxBid ?? undefined,
      cursor: cursor ?? undefined,
      limit,
    };
  }

  async function mergeBidCounts(eventSlug: string): Promise<void> {
    const seq = ++bidCountsSeq;
    try {
      const counts = await catalogApi.bidCounts(eventSlug);
      if (seq !== bidCountsSeq) return; // stale response — a newer request has superseded this one
      items.value = items.value.map((item: PublicItemSummary) => {
        const key = item.bundleId != null ? `bundle:${item.bundleId}` : String(item.itemId);
        return {
          ...item,
          bidCount: counts[key] ?? item.bidCount,
        };
      });
    } catch {
      // Keep the catalog usable if bid counts have not been deployed yet.
    }
  }

  // Every list write (fresh load, appended page, background refresh) claims
  // a slot; a response only lands if no newer write started meanwhile. With
  // pages appended on scroll, a filter change routinely lands mid-append,
  // and without this the old filter's next page is spliced onto the new
  // filter's results.
  let itemsSeq = 0;

  async function loadItems(eventSlug: string, opts: { append?: boolean } = {}): Promise<void> {
    const append = opts.append === true;
    // An append is only coherent against a settled list: mid-reload,
    // nextCursor still belongs to the previous filters.
    if (append && (!nextCursor.value || loadingMore.value || itemsState.value !== 'success')) {
      return;
    }
    const seq = ++itemsSeq;
    slug.value = eventSlug;
    loadMoreFailed.value = false;
    if (append) {
      loadingMore.value = true;
    } else {
      loadingMore.value = false;
      itemsState.value = 'loading';
      error.value = null;
    }
    try {
      const page = await catalogApi.items(eventSlug, buildQuery(append ? nextCursor.value : null));
      if (seq !== itemsSeq) return;
      items.value = append ? appendUniqueLots(items.value, page.items) : page.items;
      nextCursor.value = page.pageInfo.nextCursor;
      if (!append) loadedFor.value = listKey(eventSlug);
      // Await bid counts before flipping to 'success' so the grid renders
      // once with accurate counts, instead of rendering items first and
      // visibly patching counts in afterward (GT1-357).
      await mergeBidCounts(eventSlug);
      if (seq !== itemsSeq) return;
      itemsState.value = 'success';
    } catch (e) {
      if (seq !== itemsSeq) return;
      if (append && e instanceof ApiError && e.status === INVALID_CURSOR_STATUS) {
        await refreshItems(eventSlug, { recoveringCursor: true });
      } else if (append) {
        // The list already on screen is still good; offer a retry beneath it.
        loadMoreFailed.value = true;
      } else {
        itemsState.value = 'error';
        error.value = e instanceof Error ? e.message : 'Failed to load catalog';
      }
    } finally {
      if (seq === itemsSeq) loadingMore.value = false;
    }
  }

  // Re-fetch every item currently loaded, in place. For catching up a list
  // that is already on screen (returning from an item page, a websocket
  // reconnect): a fresh loadItems would collapse it to the first page and
  // throw the bidder back up the list. Failures keep a list that is already
  // on screen, but must still settle any other state: a refresh claims the
  // slot of a load still in flight, so swallowing its failure would leave
  // that load's skeleton up forever.
  async function refreshItems(
    eventSlug: string,
    opts: { recoveringCursor?: boolean } = {},
  ): Promise<void> {
    const target = Math.max(items.value.length, PAGE_SIZE);
    const seq = ++itemsSeq;
    loadingMore.value = opts.recoveringCursor === true;
    try {
      const fresh: PublicItemSummary[] = [];
      let cursor: string | null = null;
      do {
        const page = await catalogApi.items(
          eventSlug,
          buildQuery(cursor, Math.min(target - fresh.length, MAX_PAGE_SIZE)),
        );
        if (seq !== itemsSeq) return;
        fresh.push(...page.items);
        cursor = page.pageInfo.nextCursor;
      } while (cursor && fresh.length < target);
      items.value = fresh;
      nextCursor.value = cursor;
      loadedFor.value = listKey(eventSlug);
      await mergeBidCounts(eventSlug);
      if (seq !== itemsSeq) return;
      itemsState.value = 'success';
      loadMoreFailed.value = false;
    } catch (e) {
      if (seq !== itemsSeq) return;
      if (opts.recoveringCursor) loadMoreFailed.value = true;
      if (itemsState.value === 'success') return;
      itemsState.value = 'error';
      error.value = e instanceof Error ? e.message : 'Failed to load catalog';
    } finally {
      if (seq === itemsSeq) loadingMore.value = false;
    }
  }

  async function loadCategories(eventSlug: string): Promise<void> {
    if (categories.value.length > 0 && slug.value === eventSlug) return;
    try {
      categories.value = await catalogApi.categories(eventSlug);
    } catch {
      // Categories are optional UI; failures don't block the page.
      categories.value = [];
    }
  }

  // Shared across loadItemDetail/loadBundleDetail/refreshItemDetail/
  // refreshBundleDetail: all four write the same `detail` ref from an
  // unordered async response. Without a guard, a slow reconnect refresh for
  // the entity the bidder just navigated away from can resolve after the
  // new entity's load and clobber it with the old one's data. Each function
  // stamps its own sequence number before awaiting and only writes if it's
  // still the most recent call by the time the response lands — whichever
  // load/refresh started last always wins.
  let detailSeq = 0;

  async function loadItemDetail(eventSlug: string, itemId: number): Promise<void> {
    slug.value = eventSlug;
    detailState.value = 'loading';
    error.value = null;
    detail.value = null;
    clearRemovedContext();
    const seq = ++detailSeq;
    try {
      const fresh = await catalogApi.itemDetail(eventSlug, itemId);
      if (seq !== detailSeq) return;
      detail.value = fresh;
      detailState.value = 'success';
    } catch (e) {
      if (seq !== detailSeq) return;
      detail.value = null;
      detailState.value = isNotFound(e) ? 'not-found' : 'error';
      error.value = e instanceof Error ? e.message : 'Failed to load item';
    }
  }

  async function loadBundleDetail(eventSlug: string, bundleId: number): Promise<void> {
    slug.value = eventSlug;
    detailState.value = 'loading';
    error.value = null;
    detail.value = null;
    clearRemovedContext();
    const seq = ++detailSeq;
    try {
      const fresh = await catalogApi.bundleDetail(eventSlug, bundleId);
      if (seq !== detailSeq) return;
      detail.value = fresh;
      detailState.value = 'success';
    } catch (e) {
      if (seq !== detailSeq) return;
      detail.value = null;
      detailState.value = isNotFound(e) ? 'not-found' : 'error';
      error.value = e instanceof Error ? e.message : 'Failed to load bundle';
    }
  }

  // Background reconcile for a bundle already on screen (GT1-446) — unlike
  // loadBundleDetail, never blanks `detail` up front and never touches it on
  // failure: a transient network blip during a fire-and-forget realtime
  // refresh must not replace a perfectly good, currently-rendered bundle
  // with an error/empty state. Also does not call clearRemovedContext(): the
  // same void action fans out on both bundle:{id} and event:{uuid}, and this
  // page subscribes to both, so resetting handledVoidBidIds mid-fan-out
  // would let the duplicate copy re-trigger another refresh.
  //
  // Guards on detailSeq like the loaders above, but does NOT claim its own
  // slot up front — it fills in an entity already loaded, so it must yield
  // to a concurrent load/navigation rather than race it for the slot.
  async function refreshBundleDetail(eventSlug: string, bundleId: number): Promise<void> {
    const seq = detailSeq;
    try {
      const fresh = await catalogApi.bundleDetail(eventSlug, bundleId);
      if (seq !== detailSeq) return;
      detail.value = fresh;
      detailState.value = 'success';
    } catch {
      // Keep showing the last-good detail.
    }
  }

  // Same soft-reconcile shape as refreshBundleDetail, for items (GT1-371
  // reconnect catch-up). ItemDetailView calls this instead of loadItemDetail
  // on a websocket reconnect: loadItemDetail's up-front `detail.value = null`
  // is correct for a real navigation but destroys and re-skeletons an
  // already-rendered page for what should be an invisible background sync.
  async function refreshItemDetail(eventSlug: string, itemId: number): Promise<void> {
    const seq = detailSeq;
    try {
      const fresh = await catalogApi.itemDetail(eventSlug, itemId);
      if (seq !== detailSeq) return;
      detail.value = fresh;
      detailState.value = 'success';
    } catch {
      // Keep showing the last-good detail.
    }
  }

  // For callers that can tell an entity is unreachable without asking the
  // server — e.g. a route :id that is not a number. Lands on the same
  // terminal state a 404 produces so there is exactly one not-found
  // rendering path; leaving detailState 'idle' instead renders nothing.
  function markDetailNotFound(): void {
    detail.value = null;
    detailState.value = 'not-found';
    error.value = null;
    clearRemovedContext();
  }

  function setFilters(next: Partial<Filters>): void {
    filters.value = { ...filters.value, ...next };
  }

  function clearFilters(): void {
    filters.value = { q: '', categoryId: null, minBid: null, maxBid: null };
  }

  // PublicItemDetail still exposes a single `id` (self-referencing for
  // bundles, same as PublicItemSummary used to) — pair it with `isBundle`
  // to build an itemId/bundleId ref comparable against WS frame payloads
  // without risking an item/bundle id collision.
  function detailEntityRef(d: PublicItemDetail): { itemId: number | null; bundleId: number | null } {
    return d.isBundle ? { itemId: null, bundleId: d.id } : { itemId: d.id, bundleId: null };
  }

  /**
   * Reconcile a WS frame against the in-memory item list + open detail.
   * Per api-contracts §7.4 the server emits one canonical state-changing
   * frame per bid (`item.high_bid_changed`); `bid.placed` is the audit
   * frame. We accept either and prefer the version-stamped frame when
   * both arrive.
   */
  function applyFrame(frame: WsClientFrame): void {
    if (frame.type === 'item.high_bid_changed' || frame.type === 'bid.placed') {
      const payload = frame.payload as
        | {
            itemId?: number | null;
            bundleId?: number | null;
            currentHighBidCents?: number;
            amountCents?: number;
            bidCount?: number;
            // bid.placed adds the following audit fields:
            bidId?: number;
            bidderId?: number;
            bidderUuid?: string;
            bidderDisplay?: string;
            placedAt?: string;
            bidType?: string;
          }
        | undefined;
      if (!payload || (payload.itemId == null && payload.bundleId == null)) return;
      const matchesEntity = (row: { itemId?: number | null; bundleId?: number | null }): boolean =>
        payload.itemId != null ? row.itemId === payload.itemId : row.bundleId === payload.bundleId;
      const newHigh = payload.currentHighBidCents ?? payload.amountCents;
      const newCount = payload.bidCount;
      if (newHigh == null) return;
      items.value = items.value.map((item) =>
        matchesEntity(item)
          ? { ...item, currentHighBidCents: newHigh, bidCount: newCount ?? item.bidCount }
          : item,
      );
      if (detail.value && matchesEntity(detailEntityRef(detail.value))) {
        // Prepend a new history entry only from the entity-scoped channel
        // (item:{id} or bundle:{id}). bid.placed fans out on both
        // event:{uuid} and item:{id}/bundle:{id}; without this guard the
        // same bid appears twice in the list.
        // GT1-469 — fan-out is async now, so the frame can also land after a
        // fetch that already lists this bid; historyHasBid dedups on bidId.
        // The reverse order is self-healing: loadItemDetail/loadBundleDetail
        // replace `detail` wholesale, dropping any frame-derived entry.
        const isPlaced = frame.type === 'bid.placed';
        const channel = (frame as { channel?: string }).channel ?? '';
        const isItemChannel = channel.startsWith('item:') || channel.startsWith('bundle:');
        let nextHistory = detail.value.bidHistory;
        if (
          isPlaced &&
          isItemChannel &&
          payload.bidderDisplay &&
          payload.amountCents != null &&
          payload.placedAt &&
          !historyHasBid(detail.value.bidHistory, payload.bidId)
        ) {
          const newEntry: PublicBidEntry = {
            ...(payload.bidId != null ? { bidId: payload.bidId } : {}),
            amountCents: payload.amountCents,
            bidderHandle: payload.bidderDisplay,
            placedAt: payload.placedAt,
            isMine: payload.bidderUuid != null && payload.bidderUuid === session.bidder?.uuid,
          };
          // Cap at 10 to match the server-side initial-load cap.
          nextHistory = [newEntry, ...detail.value.bidHistory].slice(0, 10);
          // GT1-442 §6.3 — a re-bid after removal is an ordinary placement, so
          // the "your bid was removed" context retires the moment MY new bid
          // lands. No lingering special state.
          if (newEntry.isMine) myBidRemoved.value = false;
        }
        detail.value = {
          ...detail.value,
          currentHighBidCents: newHigh,
          bidCount: newCount ?? detail.value.bidCount,
          bidHistory: nextHistory,
        };
      }
    } else if (frame.type === 'item.status_changed') {
      const payload = frame.payload as
        | {
            itemId?: number | null;
            bundleId?: number | null;
            status: PublicItemSummary['status'];
            closedAt: string | null;
          }
        | undefined;
      if (!payload || (payload.itemId == null && payload.bundleId == null)) return;
      const matchesEntity = (row: { itemId?: number | null; bundleId?: number | null }): boolean =>
        payload.itemId != null ? row.itemId === payload.itemId : row.bundleId === payload.bundleId;
      items.value = items.value.map((item) =>
        matchesEntity(item)
          ? { ...item, status: payload.status, closedAt: payload.closedAt }
          : item,
      );
      if (detail.value && matchesEntity(detailEntityRef(detail.value))) {
        detail.value = { ...detail.value, status: payload.status, closedAt: payload.closedAt };
      }
    } else if (frame.type === 'bid.voided') {
      // GT1-442 (bid-void-design.md §6.1) — an organizer removed one or more
      // bids on this entity. Bidder-facing wording is "Removed"; the payload's
      // `reason` is admin-only free text and is deliberately never read here.
      const payload = frame.payload as
        | {
            bidId?: number;
            voidedBidIds?: number[];
            itemId?: number | null;
            bundleId?: number | null;
            bidderUuid?: string;
            amountCents?: number;
            recalculatedHighBidCents?: number;
          }
        | undefined;
      if (!payload || (payload.itemId == null && payload.bundleId == null)) return;
      const actionKey = payload.bidId ?? payload.voidedBidIds?.[0] ?? null;
      if (actionKey !== null) {
        if (handledVoidBidIds.has(actionKey)) return;
        handledVoidBidIds.add(actionKey);
      }
      const matchesVoidedEntity = (row: {
        itemId?: number | null;
        bundleId?: number | null;
      }): boolean =>
        payload.itemId != null ? row.itemId === payload.itemId : row.bundleId === payload.bundleId;

      // The browse list is patched FIRST, independently of the open detail.
      // A void only reaches a bidder sitting on the catalog through
      // `event:{uuid}` (CatalogPage subscribes to that channel alone), and the
      // trailing item.high_bid_changed — the frame that normally carries a
      // price correction to the list — is published on `item:{id}` only. So
      // this frame is the ONLY chance to correct a card price after a void;
      // returning early on "no detail open" left the card advertising the
      // removed bid's amount, and ItemCard derives its Bid CTA from that price
      // (found in ver-ui).
      //
      // bidCount is deliberately left alone: the authoritative post-void count
      // rides on item.high_bid_changed, which SETS (never decrements) it, so a
      // card that also receives that frame converges without double-counting.
      if (payload.recalculatedHighBidCents != null) {
        const newHigh = payload.recalculatedHighBidCents;
        items.value = items.value.map((item) =>
          matchesVoidedEntity(item) ? { ...item, currentHighBidCents: newHigh } : item,
        );
      }

      // Everything below is scoped to the item/bundle the bidder has open.
      if (!detail.value) return;
      if (!matchesVoidedEntity(detailEntityRef(detail.value))) return;
      const mine = payload.bidderUuid != null && payload.bidderUuid === session.bidder?.uuid;
      if (mine) myBidRemoved.value = true;
      if (payload.amountCents != null) {
        // New Set so the ref-based computed in the view re-evaluates.
        const next = new Set(removedBidKeys.value);
        next.add(historyKey(mine, payload.amountCents));
        removedBidKeys.value = next;
      }
      if (payload.recalculatedHighBidCents != null) {
        // GT1-446 — drop the voided rows from bidHistory, not just the price.
        // `viewerIsHighBidder` (features/bid/store.ts) reads bidHistory[0].isMine
        // directly; leaving a voided entry on top kept a bidder pinned as
        // "leading" (and the standard-bid button hidden) until a hard refresh
        // replaced bidHistory wholesale via loadItemDetail/loadBundleDetail.
        const voidedIds = payload.voidedBidIds;
        detail.value = {
          ...detail.value,
          currentHighBidCents: payload.recalculatedHighBidCents,
          bidHistory:
            voidedIds && voidedIds.length > 0
              ? detail.value.bidHistory.filter((h) => h.bidId == null || !voidedIds.includes(h.bidId))
              : detail.value.bidHistory,
        };
      }

      // GT1-446 — a bundle bid.voided frame can come from either a plain
      // per-bid admin void (composition unchanged) or a composition-change
      // void (withdraw/restore/remove/add member — membership, photos and
      // totals can differ from what's currently rendered). The patches above
      // only correct price/history, not membership, so reconcile the bundle
      // wholesale in the background regardless of which triggered it — a
      // harmless extra request on the plain-void path, where it just
      // reconfirms the same member list. Fire-and-forget: applyFrame is sync.
      if (payload.bundleId != null && slug.value) {
        void refreshBundleDetail(slug.value, payload.bundleId);
      }
    }
  }

  return {
    slug,
    items,
    categories,
    detail,
    nextCursor,
    itemsState,
    detailState,
    error,
    loadingMore,
    loadMoreFailed,
    filters,
    filtersActive,
    hasLoadedList,
    loadItems,
    refreshItems,
    loadCategories,
    loadItemDetail,
    loadBundleDetail,
    refreshItemDetail,
    refreshBundleDetail,
    markDetailNotFound,
    setFilters,
    clearFilters,
    applyFrame,
    myBidRemoved,
    isBidHistoryEntryRemoved,
  };
});
